import React, { ChangeEvent, FormEvent, useState } from 'react';
import { ProfileRecordStatus } from '@nkr/enums/profile-record-status';
import { Button, Col, ControlLabel, Form, FormControl, FormGroup, Modal, Panel, Radio } from 'react-bootstrap';
import useCenterByDistanceList from '@nkr/store/center/hooks/use-center-by-distance-list';
import useTransferPatient from '@nkr/store/recipient/hooks/transfer-patient-hook';
import { toast } from 'react-toastify';

interface IProps {
    medicalRecordId: number,
    medicalRecordCenterId: number,
    firstName: string,
    lastName: string,
    micrositeRecordStatus: ProfileRecordStatus,
    recipientRecordStatus: ProfileRecordStatus,
    onFinished : ()=> void,
    onCancel: () => void
}

const SubmitTransferRequest = ({ medicalRecordId, medicalRecordCenterId, firstName, lastName, micrositeRecordStatus, recipientRecordStatus, onFinished, onCancel }: IProps) => {
    const [distance, setDistance] = useState(300)
    const [centerId, setCenterId] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);

    const { loading, error, requestTransfer } = useTransferPatient();

    const { list } = useCenterByDistanceList(medicalRecordCenterId, distance)

    const handleDistanceChange = (e: FormEvent<Radio & HTMLInputElement>) => {
        setDistance(parseInt(e.currentTarget.value));
    };

    const handleCenterChange = (e: ChangeEvent<FormControl & HTMLSelectElement>) => {
        let newCenterId = e.currentTarget.value;
        setCenterId(newCenterId);
    };

    const handleTransferRequest = () => {
        setShowConfirmation(false);

        requestTransfer(medicalRecordId, medicalRecordCenterId, parseInt(centerId)).then(response => {
            if (response.data.success) {
                toast.success(response.data.message);
                onFinished();
            } else {
                toast.error(response.data.message);
            }
        });
    };

    let centerAlias = '';
    let isMicrositeParticipant = true;

    if (centerId != '') {
        let center = list.find(x => x.id == parseInt(centerId));

        if (center != null) {
            centerAlias = center.alias;
            isMicrositeParticipant = center.isMicrositeParticipant;
        }
    }

    return (
        <>
            <Panel>
                <Panel.Heading>Transfer Patient</Panel.Heading>

                <Panel.Body>
                    <Form horizontal>
                        <FormGroup>
                            <Col sm={3} componentClass={ControlLabel}>Show Centers Within:</Col>
                            <Col sm={9}>
                                <div>
                                    <Radio inline id='distance-100' name='distance' type='radio' value={100} checked={distance == 100} onChange={handleDistanceChange}>100 miles</Radio>
                                    <Radio inline id='distance-300' name='distance' type='radio' value={300} checked={distance == 300} onChange={handleDistanceChange}>300 miles</Radio>
                                    <Radio inline id='distance-1000' name='distance' type='radio' value={1000} checked={distance == 1000} onChange={handleDistanceChange}>1,000 miles</Radio>
                                    <Radio inline id='distance-any' name='distance' type='radio' value={0} checked={distance == 0} onChange={handleDistanceChange}>Any Distance</Radio>
                                </div>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col sm={3} componentClass={ControlLabel}>Center:</Col>
                            <Col sm={9}>
                                <FormControl
                                    componentClass='select'
                                    value={centerId}
                                    onChange={handleCenterChange}
                                >
                                    <option value={''}>- Select Center -</option>
                                    {list.map(item =>
                                        <option key={'ctr-' + item.id} value={item.id}>{item.alias} ({item.distanceInMiles})</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>
                    </Form>

                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button bsStyle='primary' bsSize='sm' disabled={centerId == ''} onClick={() => setShowConfirmation(true)}>Request Transfer</Button>&nbsp;
                        <Button bsSize='sm' onClick={onCancel}>Cancel</Button>
                    </div>
                </Panel.Body>
            </Panel>

            <Modal backdrop='static' show={showConfirmation} onHide={() => setShowConfirmation(false)}>
                <Modal.Header closeButton>
                    Confirm Transfer of Patient
                </Modal.Header>
                <Modal.Body>
                    <div style={{ marginBottom: 5 }}>Please confirm the following:</div>
                    <ul>
                        <li>Transfer <strong>{firstName} {lastName}</strong> to {centerAlias}</li>

                        {micrositeRecordStatus == ProfileRecordStatus.Exists && isMicrositeParticipant && (
                            <li>Transfer microsite to {centerAlias}</li>
                        )}

                        {micrositeRecordStatus == ProfileRecordStatus.Exists && !isMicrositeParticipant && (
                            <li>Convert microsite to a non-center microsite</li>
                        )}

                        {recipientRecordStatus == ProfileRecordStatus.Exists && (
                            <li>Transfer recipient record to {centerAlias}</li>
                        )}
                    </ul>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle='primary' onClick={handleTransferRequest}>Submit Transfer Request</Button>
                    <Button onClick={() => setShowConfirmation(false)}>Cancel</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default SubmitTransferRequest;